// OAuth do MCP (docs/specs/flows/dashboard-api-mcp.md R13). Módulo puro, sem
// imports Deno/URL: o front (tela /oauth/consent, via src/lib/oauthConsent.ts)
// e a edge function `mcp` aplicam a MESMA allowlist. O registro de cliente
// no Supabase Auth é aberto (DCR), então quem pode receber o código é
// decidido aqui, pelo redirect — nunca pelo nome que o cliente declara.

/**
 * Hosts HTTPS do Claude (claude.ai, claude.com; Desktop e celular usam o
 * mesmo callback). Loopback (`http://localhost`) fica de fora de propósito:
 * qualquer app local registraria um cliente com ele e passaria por "Claude".
 * Claude Code conecta com chave `lv_` (cabeçalho), não precisa de OAuth.
 */
const CLAUDE_HOSTS: ReadonlySet<string> = new Set(["claude.ai", "claude.com"]);

/**
 * Redirect de cliente OAuth aceito para o MCP: só `https://claude.ai` e
 * `https://claude.com` (porta padrão). Compara o host já
 * parseado (nada de `startsWith`, que deixa passar `claude.ai.evil.com`) e
 * recusa credenciais na URL (`https://claude.ai@evil.com`).
 */
export function isAllowedOAuthRedirect(uri: string | null | undefined): boolean {
  if (!uri) return false;
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return false;
  }
  if (url.username || url.password || url.port) return false;
  return url.protocol === "https:" && CLAUDE_HOSTS.has(url.hostname);
}

/** Cliente aceito só se TODOS os redirects registrados forem do Claude. */
export function isAllowedOAuthClient(redirectUris: readonly string[] | null | undefined): boolean {
  return !!redirectUris?.length && redirectUris.every(isAllowedOAuthRedirect);
}

/**
 * Payload de um JWT sem verificar assinatura — só para ler `client_id` de um
 * token que a edge function valida em seguida no Supabase Auth.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = new TextDecoder().decode(Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)));
    const payload: unknown = JSON.parse(json);
    return payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** `client_id` de um access token emitido pelo OAuth Server; null em token de sessão comum. */
export function oauthClientId(token: string): string | null {
  const id = decodeJwtPayload(token)?.client_id;
  return typeof id === "string" && id ? id : null;
}

/**
 * Destino interno seguro para `?next=` do login: só caminho absoluto do
 * próprio site. Barra `//evil.com` e `/\evil.com` (o navegador trata como
 * outro host), URL absoluta e esquemas como `javascript:`.
 */
export function safeNextPath(next: string | null | undefined): string | null {
  if (!next || !next.startsWith("/")) return null;
  if (next.startsWith("//") || next.startsWith("/\\")) return null;
  // Tab/quebra de linha somem no parser de URL ("/\t/evil.com" vira "//evil.com").
  for (let i = 0; i < next.length; i++) if (next.charCodeAt(i) < 0x20) return null;
  try {
    const base = "https://internal.invalid";
    const url = new URL(next, base);
    if (url.origin !== base) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}
